import moment from 'moment';
import React, { FC, useState } from 'react';
import DatePicker from '../DatePicker';
import MonthPicker from '../MonthPicker';
import { RangePickerProps } from '../interface';

import Tag from '../../Tag';
import QuarterPicker from '../QuarterPicker';
import YearPicker from '../YearPicker';
import '../index.less';

const RangePicker: FC<RangePickerProps> = (props) => {
  const {
    startProps = {},
    endProps = {},
    disableFuture = true,
    monthCellFooterRender,
    tipRender,
    onChange = () => {},
    className = '',
    picker = 'date',
    label,
    value: propsValue,
    ranges = {},
    isMonth,
    futureTime,
    futureMonth,
  } = props;
  const [startMidValue, setStartMidValue] = useState((startProps as any).defaultValue);
  const [endMidValue, setEndMidValue] = useState((endProps as any).defaultValue);
  // 页脚自定义时点击需要关闭
  const [startMidOpen, setStartMidOpen] = useState(false);
  const [endMidOpen, setEndMidOpen] = useState(false);

  /** 需要对value和startProps内的value进行判断兼容 */
  const startValue = (() => {
    if ('value' in props) {
      return propsValue?.[0];
    }
    if ('value' in startProps) {
      return startProps.value;
    }
    return startMidValue;
  })();
  /** 需要对value和endProps内的value进行判断兼容 */
  const endValue = (() => {
    if ('value' in props) {
      return propsValue?.[1];
    }
    if ('value' in endProps) {
      return endProps.value;
    }
    return endMidValue;
  })();

  const futureConfig = {
    date: futureTime,
    month: futureMonth,
  };

  const disabledStart = (startValue) => {
    if (!startValue || !endValue) {
      return disableFuture ? startValue > moment() : false;
    }
    const future = (futureConfig[picker] ?? moment()).startOf(picker).valueOf();
    const startTime = startValue.startOf(picker).valueOf();
    const endTime = endValue.startOf(picker).valueOf();
    return disableFuture ? endTime < startTime || startTime > future : endTime <= startTime;
  };
  const disabledEnd = (endValue) => {
    if (!endValue || !startValue) {
      return disableFuture ? endValue > moment() : false;
    }
    const future = (futureConfig[picker] ?? moment()).startOf(picker).valueOf();
    const startTime = startValue.startOf(picker).valueOf();
    const endTime = endValue.startOf(picker).valueOf();
    return disableFuture ? endTime < startTime || endTime > future : endTime <= startTime;
  };

  const normalConfig = {
    allowClear: false,
    tipRender,
    monthCellFooterRender,
    dropdownClassName: 'fs-calendar-picker-container',
  };
  const range = {
    date: DatePicker,
    month: MonthPicker,
    quarters: QuarterPicker,
    year: YearPicker,
  };
  const Picker = isMonth ? MonthPicker : range[picker];

  const handleStartChange = (time: any) => {
    setStartMidValue(time);
    onChange(time, 'start', [time, endValue]);
  };

  const handleEndChange = (time: any) => {
    setEndMidValue(time);
    onChange(time, 'end', [startValue, time]);
  };

  const handleStartOpenChange = (open) => {
    setStartMidOpen(open);
  };

  const handleEndOpenChange = (open) => {
    setEndMidOpen(open);
  };

  /** 预设范围 */
  const handleTagClick = (tag: string) => {
    setEndMidOpen(false);
    setStartMidOpen(false);
    const [start, end] = ranges[tag] || [];
    // 如果value为数组的情况只需要onChange一次，前面两个参数无效
    onChange(start, 'start', ranges[tag] || []);
    if ('value' in props) return;
    onChange(end, 'end', ranges[tag] || []);
  };

  const renderExtraFooter = () => {
    return (
      <div style={{ textAlign: 'left' }}>
        {Object.keys(ranges).map((item) => {
          return (
            <Tag
              color="rgba(217,217,217,0.4)"
              key={item}
              type="default"
              style={{ marginRight: '4px', cursor: 'pointer' }}
              onClick={() => handleTagClick(item)}
            >
              {item}
            </Tag>
          );
        })}
      </div>
    );
  };

  /** 对于季度增加额外参数 */
  const getValue = (type: string, value, limit) => {
    if (picker === 'quarters') {
      return {
        ...value,
        limit,
        type,
      };
    }
    return {
      value,
    };
  };
  return (
    <div className={`fs-rangePicker ${className}`}>
      {label ? <label style={{ marginRight: '4px' }}>{label}</label> : null}
      <Picker
        {...normalConfig}
        disableFuture
        open={startMidOpen}
        onOpenChange={handleStartOpenChange}
        onDropdownVisibleChange={handleStartOpenChange}
        disabledDate={disabledStart}
        onChange={handleStartChange}
        renderExtraFooter={
          !ranges || JSON.stringify(ranges) === '{}' || !['date', 'month'].includes(picker) ? null : renderExtraFooter
        }
        {...startProps}
        {...getValue('start', startValue, endValue)}
      />
      <span> - </span>
      <Picker
        {...normalConfig}
        disableFuture
        open={endMidOpen}
        disabledDate={disabledEnd}
        onChange={handleEndChange}
        onOpenChange={handleEndOpenChange}
        onDropdownVisibleChange={handleEndOpenChange}
        renderExtraFooter={
          !ranges || JSON.stringify(ranges) === '{}' || !['date', 'month'].includes(picker) ? null : renderExtraFooter
        }
        {...endProps}
        {...getValue('end', endValue, startValue)}
      />
    </div>
  );
};

export default RangePicker;
